import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Typography,
  Button,
  Form,
  Input,
  Select,
  Switch,
  Tooltip
} from 'antd';
import React, { Fragment, useState } from 'react';
import {
  DeleteOutlined,
  ExportOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { apiClient } from '@api/client';
import type { IntegrationInstallInstructionsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import CopyableInput from '@components/CopyableInput/CopyableInput';
import { LtiClientRoleDto } from '@api/Api';
import { nonNull } from '@helpers/non-null';
import { useOrganization } from '@components/OrganizationBoundary';
import {
  SetupLayout,
  SetupSection,
  SetupSteps
} from '@pages/AdminSettings/components/IntegrationTab/setup-layout';
import { IntegrationsLtiIcon } from '@icons';

const { Paragraph, Text, Link } = Typography;

const LMS_HELP = [
  {
    name: 'Canvas',
    href: 'https://community.canvaslms.com/t5/Admin-Guide/How-do-I-add-an-app-using-Dynamic-Registration-in-Canvas-Apps/ta-p/648045'
  },
  {
    name: 'Moodle',
    href: 'https://docs.moodle.org/en/LTI_External_tools#Registering_an_LTI_Advantage_tool'
  },
  {
    name: 'Blackboard',
    href: 'https://help.blackboard.com/Learn/Administrator/SaaS/Integrations/Learning_Tools_Interoperability'
  },
  {
    name: 'Brightspace',
    href: 'https://community.d2l.com/brightspace/kb/articles/23662-tool-registration-deployment-and-links'
  },
  {
    name: 'Sakai',
    href: 'https://sakai.screenstepslive.com/s/sakai_help/m/114555/l/1629109-how-do-i-set-up-an-lti-advantage-tool-using-dynamic-registration'
  }
];

interface RoleMapping {
  ltiRole: string;
  role: 'organization://admin' | LtiClientRoleDto['workspaceRole'];
}

const DEFAULT_ROLE_MAPPINGS: RoleMapping[] = [
  { ltiRole: 'Administrator', role: 'organization://admin' },
  { ltiRole: 'Instructor', role: 'workspace://team-lead' },
  { ltiRole: 'Learner', role: 'workspace://contributor' }
];

type ViewState =
  | {
      type: 'mapping';
    }
  | {
      type: 'instructions';
      integrationId: string;
      url: string;
    }
  | {
      type: 'result';
      integrationId: string;
      url: string;
    };

export default function LtiInstallInstructions({
  organizationId,
  onClose
}: IntegrationInstallInstructionsProps) {
  const createRegistrationUrl = useMutation({
    mutationFn: async ({
      trustEmail,
      roles
    }: {
      trustEmail: boolean;
      roles: RoleMapping[];
    }) => {
      const { data } =
        await apiClient.integration.ltiIntegrationControllerCreateRegistrationUrl(
          {
            organizationId,
            trustEmail,
            roles: roles.map((x) => ({
              ltiRole: x.ltiRole,
              organizationRole:
                x.role === 'organization://admin'
                  ? x.role
                  : 'organization://workspace-member',
              workspaceRole:
                x.role === 'organization://admin'
                  ? 'workspace://team-lead'
                  : x.role
            }))
          }
        );
      return data;
    }
  });

  const [view, setView] = useState<ViewState>({ type: 'mapping' });

  const resultIntegrationId =
    view.type === 'result' ? view.integrationId : undefined;
  const {
    data: registrationInfo,
    isLoading: resultIsLoading,
    isFetching: resultIsFetching,
    refetch
  } = useQuery({
    enabled: !!resultIntegrationId,
    queryKey: ['lti', 'registration-info', resultIntegrationId],
    queryFn: async () => {
      const { data } =
        await apiClient.integration.ltiIntegrationControllerRegistrationInfo({
          integrationId: nonNull(resultIntegrationId)
        });
      return data;
    },
    refetchOnWindowFocus: false
  });

  const [roleMappingForm] = Form.useForm<{
    roles: RoleMapping[];
    trustEmail: boolean;
  }>();

  const { currentOrganization } = useOrganization(true);
  const canTrustEmail = !!currentOrganization.entity?.ownDomains?.length;

  const layout = {
    logo: IntegrationsLtiIcon,
    title: 'Set up LTI 1.3',
    subtitle:
      'Teachers and learners open StreamWork from their courses and sign in with the LMS.',
    progress: {
      current:
        view.type === 'mapping' ? 0 : view.type === 'instructions' ? 1 : 2,
      items: ['Roles', 'Register in your LMS', 'Verify']
    }
  };

  if (view.type === 'mapping') {
    return (
      <SetupLayout
        {...layout}
        footer={
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={createRegistrationUrl.isLoading}
              onClick={() => roleMappingForm.submit()}
            >
              Continue
            </Button>
          </>
        }
      >
        <Form
          form={roleMappingForm}
          layout="vertical"
          initialValues={{
            trustEmail: canTrustEmail,
            roles: DEFAULT_ROLE_MAPPINGS
          }}
          onFinish={async (values) => {
            const { integrationId, url } =
              await createRegistrationUrl.mutateAsync(values);
            setView({
              type: 'instructions',
              integrationId,
              url
            });
          }}
        >
          <div className="integration_setup-card" style={{ marginBottom: 24 }}>
            <div className="integration_setup-row">
              <div className="integration_setup-row-text">
                <Text strong id="lti-trust-email">
                  Trust institution email addresses
                </Text>
                <Text type="secondary">
                  StreamWork trusts the email addresses the LMS provides, so new
                  users from the LMS skip the email verification step.
                </Text>
              </div>
              <div className="integration_setup-row-control">
                <Tooltip
                  overlayClassName="toggle_favorite_overlay"
                  placement="left"
                  title={
                    canTrustEmail ? null : (
                      <>
                        Please contact
                        <br />
                        <a href="mailto:support@streamwork.com">
                          support@streamwork.com
                        </a>
                        <br />
                        to enable this feature
                      </>
                    )
                  }
                >
                  <span>
                    <Form.Item
                      noStyle
                      name="trustEmail"
                      valuePropName="checked"
                    >
                      <Switch
                        aria-labelledby="lti-trust-email"
                        disabled={!canTrustEmail}
                      />
                    </Form.Item>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>

          <SetupSection
            title="Roles"
            description={
              <>
                How <strong>LTI roles</strong> become{' '}
                <strong>StreamWork roles</strong>. Adjust the suggestions as you
                need.
              </>
            }
          >
            <Form.List name="roles">
              {(fields, { add, remove }) => (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 32px',
                    columnGap: 12,
                    alignItems: 'start'
                  }}
                >
                  <Text type="secondary" style={{ marginBottom: 6 }}>
                    LTI role
                  </Text>
                  <Text type="secondary" style={{ marginBottom: 6 }}>
                    StreamWork role
                  </Text>
                  <span />
                  {fields.map((field) => (
                    <Fragment key={field.key}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'ltiRole']}
                        rules={[
                          { required: true, message: 'LTI role is required' }
                        ]}
                      >
                        <Input placeholder="e.g. Learner" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, 'role']}
                        rules={[
                          {
                            required: true,
                            message: 'StreamWork role is required'
                          }
                        ]}
                      >
                        <Select
                          placeholder="StreamWork role"
                          options={[
                            { value: 'organization://admin', label: 'Admin' },
                            {
                              value: 'workspace://team-lead',
                              label: 'Workspace lead'
                            },
                            {
                              value: 'workspace://member',
                              label: 'Workspace member'
                            },
                            {
                              value: 'workspace://contributor',
                              label: 'Contributor'
                            }
                          ]}
                        />
                      </Form.Item>
                      {fields.length > 1 ? (
                        // As tall as the fields, so it lines up with them.
                        <div className="integration_setup-row-action">
                          <Button
                            type="text"
                            aria-label="Remove mapping"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        </div>
                      ) : (
                        <span />
                      )}
                    </Fragment>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ ltiRole: '', swRole: '' })}
                    icon={<PlusOutlined />}
                    style={{ gridColumnStart: 1, gridColumnEnd: -2 }}
                  >
                    Add mapping
                  </Button>
                </div>
              )}
            </Form.List>
          </SetupSection>
        </Form>
      </SetupLayout>
    );
  }
  if (view.type === 'result' && !resultIsLoading) {
    const clientId = registrationInfo?.clientId;
    return (
      <SetupLayout
        {...layout}
        footer={
          clientId ? (
            <Button type="primary" onClick={onClose}>
              Finish
            </Button>
          ) : (
            <>
              <Button
                onClick={() =>
                  setView({
                    type: 'instructions',
                    integrationId: view.integrationId,
                    url: view.url
                  })
                }
              >
                Back to instructions
              </Button>
              <Button
                type="primary"
                loading={resultIsFetching}
                onClick={() => refetch()}
              >
                Check again
              </Button>
            </>
          )
        }
      >
        {clientId ? (
          <>
            <Alert
              type="success"
              showIcon
              message="StreamWork is registered in your LMS"
              description="You can now deploy it in courses or modules."
            />
            <SetupSection
              title="Client ID"
              description="The ID your LMS gave StreamWork."
            >
              <CopyableInput readOnly value={clientId} />
            </SetupSection>
          </>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Registration not found yet"
            description="Make sure you finished the Dynamic Registration wizard in your LMS, then check again."
          />
        )}
      </SetupLayout>
    );
  }
  return (
    <SetupLayout
      {...layout}
      footer={
        <Button
          type="primary"
          loading={view.type === 'result' && resultIsLoading}
          onClick={() => {
            setView({
              type: 'result',
              integrationId: view.integrationId,
              url: view.url
            });
            refetch();
          }}
        >
          I’ve completed the registration
        </Button>
      }
    >
      <Paragraph type="secondary">
        Register StreamWork in any LTI 1.3 LMS with{' '}
        <strong>Dynamic Registration</strong>.
      </Paragraph>
      <SetupSteps
        steps={[
          {
            title: 'Open the external tools settings',
            description: (
              <>
                In your LMS&apos;s admin area, open <em>External tools</em> (or{' '}
                <em>LTI tools</em>).
              </>
            )
          },
          {
            title: (
              <>
                Choose <strong>Add tool → Dynamic Registration</strong>
              </>
            )
          },
          {
            title: 'Paste the registration URL',
            action: <CopyableInput readOnly value={view.url} />
          },
          { title: 'Finish the wizard and save' },
          {
            title: 'Deploy StreamWork to courses or modules',
            description: (
              <>
                Your LMS may call this a <em>deployment</em> or a{' '}
                <em>placement</em>.
              </>
            )
          }
        ]}
      />
      <SetupSection title="Help for your LMS">
        <div className="integration_setup-links">
          {LMS_HELP.map((x) => (
            <Link
              key={x.name}
              href={x.href}
              target="_blank"
              rel="noreferrer"
              className="integration_setup-link"
            >
              {x.name}
              <ExportOutlined />
            </Link>
          ))}
        </div>
      </SetupSection>
    </SetupLayout>
  );
}
